import {isEqual} from 'lodash';
import memoizeOne from 'memoize-one';
import React from 'react';

import {isDevelopment} from '../../../../../libs/shared/core/src/helpers/environment/envHelpers';
import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {DataEventActionsEnum} from '../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {DataEventContext} from '../../../../../libs/shared/core/src/models/tracking/dataEventContexts';
import {OnboardingDataEventObject} from '../../../../../libs/shared/core/src/models/tracking/objectNames/onboardingDataEventObject';
import {trackDataEvent} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {getHasOnboardingPrefilledWorkspaceV3Progress} from '../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {openInBrowser} from '../../../../../srcCommon/browser/openInBrowser';
import {AppParametricSelector, AppSelector} from '../../../../../srcCommon/helpers/reselectHelpers';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {SettingsResourcePagesEnum, SettingsRootPagesEnum} from '../../../../models/url/models/types';
import {buildChannelPath} from '../../../../models/url/settingsUrlHelpers';
import {SettingsHomeDestinationsEnum} from '../../../../settings/home/settingsHomeTypes';
import {AppState} from '../../../../store/appState';
import {ThunkAction} from '../../../../store/appThunk';
import {getShouldAddFirstSharedChannel} from '../../../../store/data/channels/channelsSelectors';
import {getActivationPreferences} from '../../../../store/data/company/companySelectors';
import {getSandboxAnalyticsInbox, getSandboxChatInbox} from '../../../../store/data/inboxes/inboxesSelectors';
import {
  navigateToNewSandboxDemo,
  navigateToSandboxAnalyticsDemoAsyncAction,
  navigateToSandboxChatDemoAsyncAction,
} from '../../../../store/data/teammates/teammatesActions';
import {getCompanyHasSandbox} from '../../../../store/data/teams/teamsSelectors';
import {navigateToLinkAction} from '../../../../store/stateless/navigationActions';
import {
  getIsSandboxAnalyticsDemoComplete,
  getIsSandboxChatDemoComplete,
  getIsSandboxGeneralDemoComplete,
  getShouldAddTeamInbox,
  getShouldAddTeammate,
} from '../../../../store/stateless/startGuideSelectors';
import {InboxesTopbarTooltip} from '../../../inboxes/topbar/inboxesTopbarTooltip';
import {getAdminSetupNamespace, useDetermineShouldAddRule} from '../adminOnboardingHelpers';
import {
  connectChannelTaskDescription,
  connectChannelTaskDetails,
  connectChannelTaskTitle,
  sandboxAnalyticsTaskDescription,
  sandboxAnalyticsTaskTitle,
  sandboxChatTaskDescription,
  sandboxChatTaskTitle,
  sandboxTaskDescriptionV3,
  sandboxTaskTitleV3,
  sharedWorkspaceTaskDescription,
  sharedWorkspaceTaskTitle,
  teammatesTaskDescription,
  teammatesTaskTitle,
} from './adminOnboardingText';
import {AdminOnboardingTaskDefinition, AdminOnboardingTaskSelectorParams} from './adminOnboardingTypes';
import {OnboardingTask, OnboardingTaskGroup} from './onboardingTasks';

/*
 * Constants.
 */

/** Simulates course completion in development. */
const isLmsCourseTasksCompleteInDevelopment = true;

const adminSetupGuideContext: DataEventContext = Object.freeze({
  name: 'admin_setup_guide',
  payload: {},
});

/*
 * Sandbox basics/core demo.
 */

export const sandboxGeneralDemoTaskV3 = Object.freeze<AdminOnboardingTaskDefinition>({
  key: 'sandboxGeneralDemo',
  title: sandboxTaskTitleV3,
  description: sandboxTaskDescriptionV3,
  getIsTaskVisible: getCompanyHasSandbox,
  getIsTaskComplete: getIsSandboxGeneralDemoComplete,
  startTask: ({dispatch}) => dispatch(navigateToNewSandboxDemo('start-scenario-3-conversation-1')),
  computeTrackingObject: () => 'explore_sandbox',
});

export const sandboxGeneralDemoTaskSetupGuide = Object.freeze<AdminOnboardingTaskDefinition>({
  ...sandboxGeneralDemoTaskV3,
  title: <Trans id="admin-setup-guide-demos-screen-core-capabilities-title">Core capabilities</Trans>,
  description: (
    <Trans id="admin-setup-guide-demos-screen-core-capabilities-description">
      Resolve customer inquiries faster through collaboration and automation.
    </Trans>
  ),
  startTask: (context) => {
    trackDataEvent(
      adminSetupGuideContext,
      DataEventActionsEnum.CLICK,
      OnboardingDataEventObject.ADMIN_SETUP_GUIDE_PRODUCT_DEMOS_CORE,
    );
    return sandboxGeneralDemoTaskV3.startTask(context);
  },
});

/*
 * Sandbox analytics demo.
 */

export const sandboxAnalyticsDemoTaskV3 = Object.freeze<AdminOnboardingTaskDefinition>({
  key: 'sandboxAnalyticsDemo',
  title: sandboxAnalyticsTaskTitle,
  description: sandboxAnalyticsTaskDescription,
  getIsTaskVisible: (state) => {
    const inbox = getSandboxAnalyticsInbox(state);
    return inbox !== undefined;
  },
  getIsTaskComplete: getIsSandboxAnalyticsDemoComplete,
  startTask: ({dispatch}) => dispatch(navigateToSandboxAnalyticsDemoAsyncAction()),
  computeTrackingObject: () => 'explore_sandbox_analytics',
});

export const sandboxAnalyticsDemoTaskSetupGuide = Object.freeze<AdminOnboardingTaskDefinition>({
  ...sandboxAnalyticsDemoTaskV3,
  title: <Trans id="admin-setup-guide-demos-screen-analytics-title">Analytics</Trans>,
  description: (
    <Trans id="admin-setup-guide-demos-screen-analytics-description">
      Explore how analytics surfaces insights to help make business decisions.
    </Trans>
  ),
  startTask: (context) => {
    trackDataEvent(
      adminSetupGuideContext,
      DataEventActionsEnum.CLICK,
      OnboardingDataEventObject.ADMIN_SETUP_GUIDE_PRODUCT_DEMOS_ANALYTICS,
    );
    return sandboxAnalyticsDemoTaskV3.startTask(context);
  },
});

/*
 * Sandbox chat demo.
 */

export const sandboxChatDemoTaskV3 = Object.freeze<AdminOnboardingTaskDefinition>({
  key: 'sandboxChatDemo',
  title: sandboxChatTaskTitle,
  description: sandboxChatTaskDescription,
  getIsTaskVisible: (state) => {
    const inbox = getSandboxChatInbox(state);
    return inbox !== undefined;
  },
  getIsTaskComplete: getIsSandboxChatDemoComplete,
  startTask: ({dispatch}) => dispatch(navigateToSandboxChatDemoAsyncAction()),
  computeTrackingObject: () => 'explore_sandbox_chat',
});

export const sandboxChatDemoTaskSetupGuide = Object.freeze<AdminOnboardingTaskDefinition>({
  ...sandboxChatDemoTaskV3,
  title: <Trans id="admin-setup-guide-demos-screen-live-chat-kb-title">Live chat and knowledge base</Trans>,
  description: (
    <Trans id="admin-setup-guide-demos-screen-live-chat-kb-description">
      Utilize Chat, Knowledge Base, and other functionalities to reduce and streamline workload.
    </Trans>
  ),
  startTask: (context) => {
    trackDataEvent(
      adminSetupGuideContext,
      DataEventActionsEnum.CLICK,
      OnboardingDataEventObject.ADMIN_SETUP_GUIDE_PRODUCT_DEMOS_CHAT,
    );
    return sandboxChatDemoTaskV3.startTask(context);
  },
});

/*
 * Complete "Inboxes vs. Channels" Front Academy course
 */

export const inboxesVsChannelsCourseTaskSetupGuideUuid = '3331d5d0-f466-4ef6-b66e-a994de1ff808';

export const inboxesVsChannelsCourseTaskSetupGuide = Object.freeze<AdminOnboardingTaskDefinition>({
  key: 'inboxes-vs-channels',
  title: (
    <Trans id="admin-setup-guide-setup-screen-inboxes-vs-channels-title">
      Understand Front inboxes vs. channels
    </Trans>
  ),
  description: (
    <Trans id="admin-setup-guide-setup-screen-inboxes-vs-channels-description">
      Learn how to structure inboxes and channels to make the best use of Front.
    </Trans>
  ),
  isExternal: true,
  getIsTaskComplete: makeGetIsCourseTaskComplete(inboxesVsChannelsCourseTaskSetupGuideUuid),
  startTask: () => {
    trackDataEvent(
      adminSetupGuideContext,
      DataEventActionsEnum.CLICK,
      OnboardingDataEventObject.ADMIN_SETUP_GUIDE_BASICS_CHECKLIST_ITEM,
      {
        item: 'understand_front_inboxes_vs_channels',
      },
    );
    openInBrowser(
      `https://academy.front.com/app/courses/${encodeURIComponent(
        inboxesVsChannelsCourseTaskSetupGuideUuid,
      )}`,
    );
  },
  getIsTaskVisible: () => true,
  computeTrackingObject: () => 'understand_front_inboxes_vs_channels',
});

/*
 * Set up shared workspace.
 */

const getSharedWorkspaceTaskIsComplete: AppParametricSelector<
  AdminOnboardingTaskSelectorParams,
  boolean | undefined
> = (state, {shouldAddRule}) => {
  const shouldUseCompanyActivationPreferences = getHasOnboardingPrefilledWorkspaceV3Progress(state);

  if (shouldUseCompanyActivationPreferences) {
    const companyActivationPreferences = getActivationPreferences(state);
    return Boolean(
      companyActivationPreferences.adminOnboardingResources.circleId &&
        companyActivationPreferences.adminOnboardingResources.inboxId,
    );
  }

  const shouldAddTeamInbox = getShouldAddTeamInbox(state);

  if (shouldAddRule === undefined) {
    return undefined;
  }

  const shouldAddSharedWorkspace = shouldAddTeamInbox || shouldAddRule;
  return !shouldAddSharedWorkspace;
};

export const sharedWorkspaceTask = Object.freeze<AdminOnboardingTaskDefinition>({
  key: 'sharedWorkspace',
  title: sharedWorkspaceTaskTitle,
  description: sharedWorkspaceTaskDescription,
  getIsTaskVisible: () => true,
  getIsTaskComplete: getSharedWorkspaceTaskIsComplete,
  startTask: ({openAdminWizard}) => openAdminWizard(),
  computeTrackingObject: () => 'setup_shared_inbox',
});

export const sharedWorkspaceTaskSetupGuide = Object.freeze<AdminOnboardingTaskDefinition>({
  ...sharedWorkspaceTask,
  title: <Trans id="admin-setup-guide-setup-screen-shared-inbox-title">Set up shared inbox</Trans>,
  description: (
    <Trans id="admin-setup-guide-setup-screen-shared-inbox-description">
      Create your first shared inbox and workflows.
    </Trans>
  ),
  startTask(context) {
    trackDataEvent(
      adminSetupGuideContext,
      DataEventActionsEnum.CLICK,
      OnboardingDataEventObject.ADMIN_SETUP_GUIDE_BASICS_CHECKLIST_ITEM,
      {
        item: 'setup_shared_inbox',
      },
    );
    return sharedWorkspaceTask.startTask(context);
  },
});

/*
 * Connect channel.
 */

const isConnectChannelTaskComplete: AppSelector<boolean> = (state) => !getShouldAddFirstSharedChannel(state);

export const connectChannelTask = Object.freeze<AdminOnboardingTaskDefinition>({
  key: 'connectChannel',
  title: connectChannelTaskTitle,
  description: connectChannelTaskDescription,
  details: connectChannelTaskDetails,
  getIsTaskVisible: () => true,
  getIsTaskComplete: isConnectChannelTaskComplete,
  startTask: ({dispatch}) => dispatch(startConnectChannelTaskAction()),
  computeTrackingObject: (isTaskComplete) => (isTaskComplete ? 'view_channels' : 'add_channel'),
});

export const connectChannelTaskSetupGuide = Object.freeze<AdminOnboardingTaskDefinition>({
  ...connectChannelTask,
  title: <Trans id="admin-setup-guide-setup-screen-import-messages-title">Import team messages</Trans>,
  description: (
    <Trans id="admin-setup-guide-setup-screen-import-messages-description">
      Connect a channel, like email or chat, to start managing your team’s messages.
    </Trans>
  ),
  details: (
    <InboxesTopbarTooltip
      firstLine={
        <Trans id="admin-setup-guide-setup-screen-import-messages-info-title">
          What to expect after importing?
        </Trans>
      }
      secondLine={
        <Trans id="admin-setup-guide-setup-screen-import-messages-info-description">
          <ul>
            <li>
              Explore Front worry-free as your team can still maintain their current workflows in your
              existing tools.
            </li>
            <li>You can disconnect anytime.</li>
          </ul>
        </Trans>
      }
    />
  ),
  startTask(context) {
    trackDataEvent(
      adminSetupGuideContext,
      DataEventActionsEnum.CLICK,
      OnboardingDataEventObject.ADMIN_SETUP_GUIDE_BASICS_CHECKLIST_ITEM,
      {
        item: 'add_channel',
      },
    );
    return connectChannelTask.startTask(context);
  },
});

export function startConnectChannelTaskAction(): ThunkAction {
  return (dispatch, getState) => {
    const state = getState();
    const namespace = getAdminSetupNamespace(state);

    if (isConnectChannelTaskComplete(state)) {
      dispatch(
        navigateToLinkAction(
          buildChannelPath({
            rootPage: SettingsRootPagesEnum.CHANNELS,
            namespace,
            resourcePage: SettingsResourcePagesEnum.OVERVIEW,
          }),
        ),
      );
    } else {
      dispatch(
        navigateToLinkAction(
          buildChannelPath({
            rootPage: SettingsRootPagesEnum.CHANNELS,
            namespace,
            resourcePage: SettingsResourcePagesEnum.NEW,
          }),
        ),
      );
    }
  };
}

/*
 * Complete "Best Practices" Front Academy course
 */

export const bestPracticesCourseTaskSetupGuideUuid = 'f03e0799-52eb-4a5b-a53d-6f8f8204c07d';

export const bestPracticesCourseTaskSetupGuide = Object.freeze<AdminOnboardingTaskDefinition>({
  key: 'best-practices',
  title: (
    <Trans id="admin-setup-guide-setup-screen-managing-title">
      Learn best practices of managing Front inboxes
    </Trans>
  ),
  description: (
    <Trans id="admin-setup-guide-setup-screen-managing-description">
      Manage your inboxes like a pro by utilizing powerful Front features.
    </Trans>
  ),
  isExternal: true,
  getIsTaskComplete: makeGetIsCourseTaskComplete(bestPracticesCourseTaskSetupGuideUuid),
  startTask: () => {
    trackDataEvent(
      adminSetupGuideContext,
      DataEventActionsEnum.CLICK,
      OnboardingDataEventObject.ADMIN_SETUP_GUIDE_BASICS_CHECKLIST_ITEM,
      {
        item: 'learn_best_practices_of_managing_front_inboxes',
      },
    );
    openInBrowser(
      `https://academy.front.com/app/courses/${encodeURIComponent(bestPracticesCourseTaskSetupGuideUuid)}`,
    );
  },
  getIsTaskVisible: () => true,
  computeTrackingObject: () => 'learn_best_practices_of_managing_front_inboxes',
});

/*
 * Invite teammates.
 */

export const inviteTeammatesTask = Object.freeze<AdminOnboardingTaskDefinition>({
  key: 'inviteTeammates',
  title: teammatesTaskTitle,
  description: teammatesTaskDescription,
  getIsTaskVisible: () => true,
  getIsTaskComplete: (state) => !getShouldAddTeammate(state),
  startTask: ({openSettingsHomeShortcut}) =>
    openSettingsHomeShortcut(SettingsHomeDestinationsEnum.ADD_TEAMMATES),
  computeTrackingObject: () => 'invite_team',
});

export const inviteTeammatesTaskSetupGuide = Object.freeze<AdminOnboardingTaskDefinition>({
  ...inviteTeammatesTask,
  title: <Trans id="admin-setup-guide-setup-screen-invite-teammates-title">Invite teammates</Trans>,
  description: (
    <Trans id="admin-setup-guide-setup-screen-invite-teammates-description">
      Start collaborating using comments, assignments, and shared drafts.
    </Trans>
  ),
  startTask(context) {
    trackDataEvent(
      adminSetupGuideContext,
      DataEventActionsEnum.CLICK,
      OnboardingDataEventObject.ADMIN_SETUP_GUIDE_BASICS_CHECKLIST_ITEM,
      {
        item: 'invite_team',
      },
    );
    return inviteTeammatesTask.startTask(context);
  },
});

/*
 * Selectors.
 */

function makeGetIsCourseTaskComplete(
  uuid: string,
): AppParametricSelector<AdminOnboardingTaskSelectorParams, boolean | undefined> {
  return (_state, params) =>
    (isDevelopment() && isLmsCourseTasksCompleteInDevelopment) || params.completedCourses?.[uuid] || false;
}

const learnTasks = Object.freeze([
  sandboxGeneralDemoTaskV3,
  sandboxAnalyticsDemoTaskV3,
  sandboxChatDemoTaskV3,
]);
const setupTasks = Object.freeze([sharedWorkspaceTask, connectChannelTask, inviteTeammatesTask]);

export const getAdminOnboardingTasks: AppParametricSelector<
  AdminOnboardingTaskSelectorParams,
  ReadonlyArray<OnboardingTaskGroup>
> = memoizeOne(
  (state, params) => {
    const computeIsTaskVisible = (task: AdminOnboardingTaskDefinition) =>
      task.getIsTaskVisible(state, params);

    const createTask = (taskDefinition: AdminOnboardingTaskDefinition): OnboardingTask => {
      const isTaskComplete = taskDefinition.getIsTaskComplete(state, params);

      return {
        key: taskDefinition.key,
        title: taskDefinition.title,
        description: taskDefinition.description,
        details: taskDefinition.details ?? null,
        isTaskComplete,
        startTask: taskDefinition.startTask,
        computeTrackingEventData: () => ({
          click_object: taskDefinition.computeTrackingObject(isTaskComplete),
          user_track: 'admin',
        }),
      };
    };

    // Keep only visible tasks.
    const visibleLearnTasks = learnTasks.filter(computeIsTaskVisible);
    const visibleSetupTasks = setupTasks.filter(computeIsTaskVisible);

    // If there is only one learn task, return only one group (pre-v3-analytics).
    if (visibleLearnTasks.length <= 1) {
      return [
        {
          key: 'general',
          title: null,
          tasks: [...visibleLearnTasks, ...visibleSetupTasks].map(createTask),
        },
      ];
    }

    return [
      {
        key: 'learn',
        title: <Trans id="admin-setup-guide-group-title-learn">Learn about Front</Trans>,
        tasks: visibleLearnTasks.map(createTask),
      },
      {
        key: 'setup',
        title: <Trans id="admin-setup-guide-group-title-setup">Set up Front</Trans>,
        tasks: visibleSetupTasks.map(createTask),
      },
    ];
  },
  ([nextState, nextParams], [prevState, prevParams]) =>
    nextState === prevState && isEqual(nextParams, prevParams),
);

/*
 * Hooks.
 */

export function useAdminOnboardingTasks(): ReadonlyArray<OnboardingTaskGroup> {
  const shouldAddRule = useDetermineShouldAddRule();
  return useAppSelector((state: AppState) => getAdminOnboardingTasks(state, {shouldAddRule}));
}

export function useSharedWorkspaceTaskIsComplete(): boolean | undefined {
  const shouldAddRule = useDetermineShouldAddRule();
  return useAppSelector((state: AppState) => getSharedWorkspaceTaskIsComplete(state, {shouldAddRule}));
}
